import gsap from 'gsap'

import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { BrandMark } from '@/components/generals/logo'

interface BoutiqueLoaderProps {
    progress: number
    done: boolean
    reducedMotion: boolean
    onExited: () => void
}

const THREAD = 'M118 96 C 170 96, 170 40, 222 46 S 280 110, 330 70 S 380 20, 402 58'

// The opening screen: a yarn ball that unwinds as the 3D boutique is stitched together.
export const BoutiqueLoader: FC<BoutiqueLoaderProps> = ({ progress, done, reducedMotion, onExited }): ReactNode => {
    const root = useRef<HTMLDivElement>(null)
    const content = useRef<HTMLDivElement>(null)
    const exited = useRef(false)
    const onExitedRef = useRef(onExited)

    onExitedRef.current = onExited

    useEffect(() => {
        if (!done || exited.current || !root.current) return

        exited.current = true

        const timeline = gsap.timeline({
            onComplete: () => onExitedRef.current()
        })

        if (reducedMotion) {
            timeline.to(root.current, { autoAlpha: 0, duration: 0.3 })
        }
        else {
            timeline
                .to(content.current, { y: -18, autoAlpha: 0, duration: 0.55, ease: 'power2.in', delay: 0.25 })
                .to(root.current, { clipPath: 'inset(0% 0% 100% 0%)', duration: 1.1, ease: 'power4.inOut' }, '-=0.1')
        }

        return () => {
            timeline.kill()
        }
    }, [done, reducedMotion])

    const percent = Math.round(Math.min(1, Math.max(0, progress)) * 100)

    return (
        <div
            ref={root}
            role='status'
            aria-live='polite'
            aria-label={done ? 'Butik siap' : `Membuka butik, ${percent} persen`}
            className='cw-loader absolute inset-0 z-40 flex items-center justify-center bg-[radial-gradient(90%_70%_at_50%_40%,#fffaf3_0%,#f9ece2_55%,#f2ddd0_100%)]'
            style={{ clipPath: 'inset(0% 0% 0% 0%)' }}
        >
            <div ref={content} className='flex w-full max-w-md flex-col items-center px-8 text-center'>
                <BrandMark size={136} priority className='mb-4 shadow-[0_24px_50px_-26px_rgba(58,40,30,0.6)] ring-4 ring-white/80' />
                <svg viewBox='0 0 420 140' className='w-full max-w-[260px]' aria-hidden='true'>
                    <path
                        d={THREAD}
                        fill='none'
                        stroke='#e8a5b0'
                        strokeWidth='3'
                        strokeLinecap='round'
                        pathLength={100}
                        strokeDasharray='100'
                        strokeDashoffset={100 - percent}
                        style={{ transition: 'stroke-dashoffset 0.5s ease-out' }}
                    />
                    <g className={cn(!reducedMotion && 'origin-[70px_80px] animate-yarn-spin')}>
                        <circle cx='70' cy='80' r='46' fill='#f3c9cf' />
                        <g fill='none' stroke='#fffaf3' strokeWidth='3.2' strokeLinecap='round' opacity='0.9'>
                            <path d='M30 64c22-14 58-16 82-2' />
                            <path d='M26 82c26-16 64-16 88 0' />
                            <path d='M32 100c22-12 54-12 76 0' />
                            <path d='M52 38c-14 22-14 64 2 86' />
                            <path d='M86 38c14 22 14 64-2 86' />
                        </g>
                        <circle cx='70' cy='80' r='46' fill='none' stroke='#e8a5b0' strokeWidth='2' />
                    </g>
                    <circle cx='402' cy='58' r='5' fill='#e8a5b0' opacity={percent > 96 ? 1 : 0} style={{ transition: 'opacity 0.4s' }} />
                </svg>

                <p className='mt-2 text-xs font-bold uppercase tracking-[0.28em] text-cocoa-500'>
                    Menyiapkan butik untukmu
                </p>

                <div className='mt-7 h-[3px] w-56 overflow-hidden rounded-full bg-blush-100'>
                    <div
                        className='h-full rounded-full bg-blush-400 transition-[width] duration-500 ease-out'
                        style={{ width: `${percent}%` }}
                    />
                </div>
                <p className='mt-3 text-sm tabular-nums text-cocoa-600'>
                    {percent}%
                </p>
            </div>
        </div>
    )
}
